/**
 * Diccionario ESPAÑOL del portal. Es el idioma por defecto y la fuente del
 * tipo `Messages`: `en.ts` y `pt.ts` están obligados por el compilador a tener
 * exactamente estas claves.
 *
 * NO se traducen (a propósito): las siglas de la planilla (Tot, Ef%, Pos%, PJ,
 * Rec, Saq, P1..P6), los grados del código DataVolley (#, +, !, −, /, =), los
 * ids técnicos (`volleyball-indoor`, `SCOUT_CORS_ORIGIN`, `.dvw`) ni las
 * unidades.
 *
 * Los textos de acá son BYTE-IDÉNTICOS a los que tenía la UI antes del i18n:
 * la suite E2E localiza los controles por estos strings.
 */
export const es = {
  nav: {
    brand: 'Scout Portal',
    federation: 'Federación',
    matches: 'Partidos',
    competitions: 'Competencias',
    season: 'Temporada',
    planner: 'Rotaciones',
    teams: 'Equipos',
    settings: 'Ajustes',
    sameOrigin: 'mismo origen',
    connecting: 'Conectando…',
    connected: 'API conectado',
    connectedTitle: 'API v{version}',
    missingToken: 'Falta token',
    missingTokenTitle: 'El API exige Bearer token',
    down: 'API sin respuesta',
  },

  common: {
    loading: 'Cargando {what}…',
    loadingData: 'datos',
    retry: 'Reintentar',
    refresh: 'Actualizar',
    save: 'Guardar',
    saving: 'Guardando…',
    saved: 'Guardado',
    cancel: 'Cancelar',
    all: 'Todos',
    sets: 'Sets',
    set: 'Set {set}',
    total: 'Total',
    player: 'Jugador',
    team: 'Equipo',
    unexpectedError: 'Ocurrió un error inesperado.',
    noData: 'Sin datos',
  },

  routes: {
    unknownBefore: 'Esa dirección no existe. ',
    unknownLink: 'Ir a Partidos',
    unknownAfter: '.',
    unknownTab: 'Esa pestaña no existe.',
  },

  /**
   * Permisos: el 403 no es un error del usuario, es su rol.
   *
   * El rol `viewer` (dirigente/prensa) recibe los documentos ya digeridos
   * —resumen, planilla, reporte, live— pero NO el log crudo (`events:read`).
   * Cuando el API niega el log, el portal muestra esto y sigue andando con lo
   * que sí tiene permiso, en vez de plantar un cartel rojo.
   */
  access: {
    noEvents: 'Tu rol no ve el detalle jugada a jugada de este partido.',
    noEventsHint:
      'Sí podés ver el resumen, la planilla y el reporte estadístico. Las vistas que se arman con el log crudo (rotaciones, armador, zonas y evolución) necesitan el permiso «{permission}»: pedíselo a la federación.',
    noEventsTab: 'Esta vista se arma con el log jugada a jugada, que tu rol no puede leer.',
    noEventsLive:
      'El marcador en vivo se sigue actualizando, pero el detalle de cada jugada no: tu rol no puede leer el log.',
    noScoreboard: 'El marcador por sets se reconstruye del log, que tu rol no puede leer.',
    goToSummary: 'Ver el resumen',
  },

  matches: {
    title: 'Partidos',
    searchPlaceholder: 'Buscar por nombre o id…',
    searchLabel: 'Buscar partidos',
    loadingWhat: 'partidos',
    empty:
      'Todavía no hay partidos en el archivo. Cuando un puesto de captura sincronice, aparecen acá.',
    noMatchesFor: 'Ningún partido coincide con «{query}».',
    colMatch: 'Partido',
    colRules: 'Reglamento',
    colEvents: 'Eventos',
    colLastSeq: 'Últ. seq',
    colUpdated: 'Actualizado',
    colActions: 'Acciones',
    scoresheet: 'Planilla',
    scoresheetTitle: 'Acta de mesa del partido, lista para imprimir y firmar.',
    report: 'Reporte',
    reportTitle: 'Reporte estadístico: fundamentos, sideout/break y jugadores.',
    dvw: '.dvw',
    mesa: 'Mesa',
    mesaTitle: 'Mesa de control del acta: oficiales, challenge y firmas.',
  },

  /** Convención de eficiencia activa (se elige en Ajustes, se muestra en cada pantalla). */
  efficiency: {
    chip: 'Convención: {name}',
    chipTitle:
      'Con qué fórmulas se calculan eficiencia y positividad en TODAS las pestañas de estadística. Se cambia en Ajustes.',
  },

  /** Filtro "One Rally Before": condicionar por el rally anterior. */
  prevRally: {
    filterLabel: 'Rally anterior',
    none: 'Sin condición',
    teamLabel: 'visto desde',
    activeHint:
      'Entran sólo los rallies cuyo rally ANTERIOR (del mismo set) cumple la condición. Aplica a Resumen, Armador, Zonas y Clips; Rotaciones y Evolución no toman este recorte, y los documentos imprimibles tampoco.',
    preset_afterOwnPoint: 'tras punto propio',
    preset_afterRivalPoint: 'tras punto rival',
    preset_afterOwnBreak: 'tras punto de break propio',
    preset_afterRivalBreak: 'tras punto de break rival',
    preset_afterOwnReceptionError: 'tras error de recepción propio',
    preset_afterOwnAttackError: 'tras error de ataque propio',
    preset_afterOwnAce: 'tras ace propio',
  },

  match: {
    loadingWhat: 'el partido',
    loadingReport: 'el reporte',
    printable: 'Planilla imprimible',
    report: 'Reporte estadístico',
    downloadDvw: 'Descargar .dvw',
    noClosedSets: 'Sin sets cerrados todavía.',
    tabSummary: 'Resumen',
    tabRotations: 'Rotaciones',
    tabSetter: 'Armador',
    tabZones: 'Zonas',
    tabClips: 'Clips',
    tabEvolution: 'Evolución',
    tabLive: 'En vivo',
  },

  summary: {
    noPoints: 'Sin puntos cargados.',
    pointBy: 'Punto por',
    count: 'Cant.',
    percent: '%',
    bySkill: 'Por fundamento',
    pointsByReason: 'Puntos por razón',
    players: 'Jugadores',
    sideout: 'Sideout {pct}',
    sideoutTitle: 'Puntos ganados recibiendo / rallies recibidos',
    breakPoint: 'Break {pct}',
    breakTitle: 'Puntos ganados sacando / rallies sacados',
    pointsAttribution: 'Atribución de puntos',
    pointsOrigin: 'De dónde salió',
    pointsMeans: 'Qué quiere decir',
    pointsScoreboard: 'Puntos del marcador',
    pointsScoreboardHint: 'Lo que dice el tanteador en el recorte elegido.',
    pointsByPlayer: 'Acción propia con camiseta',
    pointsByPlayerHint: 'Ace, punto de ataque o de bloqueo, con el autor codificado.',
    pointsUnassigned: 'Acción propia sin camiseta',
    pointsUnassignedHint: 'El punto es del equipo pero el log no dice de quién.',
    pointsUnassignedNote:
      'La fila de total suma {count} punto(s) de acción propia sin camiseta, que no están en ninguna fila de jugador.',
    pointsOpponentErrors: 'Errores del rival',
    pointsOpponentErrorsHint: 'Los regaló el rival: no tienen autor propio y no se le inventa uno.',
    pointsIdentity:
      'Cierra: {byPlayer} + {unassigned} + {opponentErrors} = {total} puntos del marcador.',
    pointsMismatch:
      'El desglose no cierra con el marcador. Es un bug del reporte, no del partido: avisá antes de usar estos números.',
    pointsByPlayerTitle: 'Puntos por jugador',
    noScorers: 'Ningún jugador anotó puntos por acción propia en el recorte elegido.',
    noPointsBreakdown:
      'Este API no publica el desglose de atribución de puntos (es una versión anterior del reporte).',
    player: 'Jugador',
    ace: 'Ace',
    aceTitle: 'Puntos de saque',
    kill: 'Ataque',
    killTitle: 'Puntos de ataque',
    block: 'Bloqueo',
    blockTitle: 'Puntos de bloqueo',
    pointsChip: '{total} pts · {byPlayer} con autor',
    pointsChipTitle: 'Puntos del marcador y cuántos tienen jugador codificado',
  },

  skills: {
    empty: 'Sin fundamentos codificados.',
    noPlayers: 'Sin jugadores con acciones.',
    skill: 'Fundamento',
    total: 'Tot',
    perfect: '#',
    perfectTitle: 'Perfectos',
    errors: '=',
    errorsTitle: 'Errores',
    efficiency: 'Ef%',
    efficiencyTitle: 'Eficiencia',
    positivity: 'Pos%',
    positivityTitle: 'Perfectos / positividad en recepción',
    matchesPlayed: 'PJ',
    points: 'Pts',
    pointsTitle: 'Puntos anotados',
    names: {
      serve: 'Saque',
      reception: 'Recepción',
      set: 'Armado',
      attack: 'Ataque',
      block: 'Bloqueo',
      dig: 'Defensa',
      freeball: 'Free ball',
    },
    reasons: {
      attack: 'Ataque',
      block: 'Bloqueo',
      serve: 'Saque',
      opponent_error: 'Error rival',
      sanction: 'Sanción',
      // Motivos del acta (§2.3): el punto que sale de una demora, de un cambio
      // ilegal, de una falta de posición o de la reconciliación de un
      // challenge. Sin estas claves salían con su id técnico en pantalla.
      delay_penalty: 'Penalización por demora',
      illegal_substitution: 'Cambio ilegal',
      position_fault: 'Falta de posición',
      challenge_adjustment: 'Ajuste por challenge',
      forfeit: 'W.O.',
      other: 'Otro',
    },
  },

  rotations: {
    intro:
      'Rotación = posición del armador (convención DataVolley). El rally se cuenta en la rotación con la que se jugó; el doble cambio conserva la ranura del armador.',
    noLineup:
      'El log no trae la formación inicial (evento lineup_set), así que no se puede saber en qué rotación se jugó cada rally.',
    noSetter:
      'Hay formación, pero no se puede identificar al armador (falta setterPosition en el lineup y ningún jugador declara rol de armador en el plantel).',
    untracked: 'Rotación no reconstruible.',
    unassignedSuffix: ' ({count} rallies sin asignar a una rotación)',
    unassignedChip: '{count} sin asignar',
    unassignedTitle: 'Rallies jugados antes de conocer la formación',
    sideoutByRotation: 'Sideout por rotación',
    colRotation: 'Rot.',
    colRallies: 'Rallies',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies recibidos',
    colSideout: 'Sideout%',
    colServed: 'Saq',
    colServedTitle: 'Rallies sacados',
    colBreak: 'Break%',
    colNet: 'Neto',
    colNetTitle: 'Puntos ganados − perdidos',
    barTitle: '{label}: sideout {pct} ({won}/{total})',
  },

  setter: {
    intro:
      'El destino se identifica por la combinación del ataque; si no está codificada se usa la zona de salida y, en última instancia, la de llegada.',
    attacksSet: '{count} ataques armados',
    empty:
      'No hay ataques asociados a una recepción codificada: sin recepciones en el log esta vista queda vacía (no es un error).',
    receptionHeading: 'Recepción {grade} ({name}) — {count} ataques',
    gradePerfect: 'perfecta',
    gradePositive: 'positiva',
    gradeNeutral: 'neutra',
    gradeNegative: 'negativa',
    colTarget: 'Destino',
    colSource: 'Origen del dato',
    colAttacks: 'Ataques',
    colPercent: '%',
    colEfficiency: 'Ef%',
    uncoded: 'sin codificar',
    sourceCombo: 'combinación',
    sourceStartZone: 'zona de salida',
    sourceEndZone: 'zona de llegada',
    sourceUnknown: '—',
    segmentTitle: '{target}: {count} ataques ({pct}), eficiencia {efficiency}',
    receptionCardTitle: '{team} · recepción → punto',
    receptionCardHint:
      'Conversión = puntos ganados sobre los rallies CON recepción codificada. No es el Sideout% del resumen y de rotaciones, que va sobre todos los rallies recibidos: por eso los dos números pueden no coincidir.',
    noReceptions: 'Sin recepciones codificadas.',
    colGrade: 'Grado',
    colRallies: 'Rallies',
    colSideout: 'Puntos',
    colSideoutPct: 'Conversión%',
    noSetAttacks: 'Sin ataques armados con esta recepción.',
    phaseFilter: 'Fase',
    phaseAll: 'Las dos fases',
    phaseSideout: 'Primera intención',
    phaseTransition: 'Contraataque',
    phaseAllHint:
      'Sideout y contraataque juntos: la distribución completa del armador, condicionada al grado de recepción.',
    phaseSideoutHint:
      'Primera intención: el ataque que sale de la recepción, con todas las opciones abiertas y el bloqueo rival todavía sin leer.',
    phaseTransitionHint:
      'Contraataque re-armado tras defensa propia, donde suele quedar una sola opción. Incluye los rallies que el equipo sacó.',
    phaseTotal: '{count} ataques en la fase',
    phaseWithReception: '{count} con recepción armable',
    phaseWithReceptionTitle:
      'Ataques de rallies con recepción propia de grado #, +, ! o −: son los únicos que entran en las tablas por grado',
    phaseWithoutReception: '{count} sin recepción propia',
    phaseWithoutReceptionTitle:
      'Ataques de rallies que el equipo sacó, o con recepción / o =: existen, pero no hay grado que condicionar',
    phaseEmpty: 'Sin ataques en esta fase.',
    phaseNoGrades:
      'Los ataques de esta fase no salen de una recepción codificada de grado armable, así que no hay tabla por grado.',
    phaseAllTargets: 'Todos los destinos de la fase',
  },

  clips: {
    filterCard: 'Filtro',
    intro:
      'Cada toque codificado con timecode del VAR es un clip. Elegí el recorte y pedí los videos: los corta el server de replay de la cancha, no el portal.',
    team: 'Equipo',
    bothTeams: 'Los dos',
    player: 'Camiseta',
    allPlayers: 'Todas',
    playerNeedsTeam: 'Elegí un equipo para filtrar por camiseta.',
    skill: 'Fundamento',
    allSkills: 'Todos',
    skill_serve: 'Saque',
    skill_reception: 'Recepción',
    skill_set: 'Armado',
    skill_attack: 'Ataque',
    skill_block: 'Bloqueo',
    skill_dig: 'Defensa',
    skill_freeball: 'Freeball',
    evaluation: 'Evaluación',
    allEvaluations: 'Todas',
    rotation: 'Rotación',
    allRotations: 'Todas',
    rotationOption: 'P{position} (armador)',
    setsHint: 'El recorte de sets es el selector de arriba, el mismo de todas las pestañas.',
    camerasCard: 'Cámaras',
    camerasWhat: 'las cámaras del VAR',
    camerasError: 'No se pudo leer el catálogo de cámaras del VAR ({reason}).',
    noCameras: 'El VAR no declara ninguna cámara.',
    noVar: 'No hay server de replay configurado en este navegador.',
    noVarHint:
      'La lista de cortes se arma igual (etiqueta, timecode y duración): lo que falta es a quién pedirle el archivo. Se configura en Ajustes → Server de replay (VAR), con la URL de la cancha.',
    listCard: 'Clips',
    loadingWhat: 'los clips',
    count: '{count} clip(s)',
    skipped: '{count} toque(s) sin timecode: no hay clip posible.',
    files: '{files} archivo(s) — un corte por cada una de las {cameras} cámara(s) elegidas.',
    requestAll: 'Pedir los {count} clips',
    jobsSummary: '{done} listo(s) · {working} en curso · {failed} con error',
    emptyForFilter: 'Ningún clip para este filtro.',
    colClip: 'Clip',
    colSet: 'Set',
    colTimecode: 'Timecode',
    colDuration: 'Duración',
    colState: 'Estado',
    seconds: '{seconds} s',
    timecodeTitle: 'videoNs {ns} (reloj maestro del VAR)',
    request: 'Pedir clip',
    download: 'Descargar',
    failed: 'Falló',
    state_pending: 'Pidiendo…',
    state_queued: 'En cola',
    state_running: 'Cortando',
  },

  zones: {
    intro:
      '«s/d» agrupa los toques sin esa coordenada codificada: no se descartan, para que el total cierre. El color es la eficiencia observada en la propia matriz y el número exacto va escrito en cada celda.',
    skillFilter: 'Fundamento',
    skillAttack: 'Ataque',
    skillServe: 'Saque',
    skillReception: 'Recepción',
    introAttack: 'Filas = zona de salida del ataque, columnas = zona de llegada.',
    introServe: 'Filas = zona desde donde se sacó, columnas = zona a la que se mandó el saque.',
    introReception:
      'Filas = zona de donde vino el saque, columnas = zona donde se recibió. Los «perfectos» acá son la positividad (# + +), que es lo que se mira en cancha.',
    playerFilter: 'Jugador',
    serverFilter: 'Sacador',
    receiverFilter: 'Receptor',
    wholeTeam: 'Todo el equipo',
    emptyTeam: 'Sin ataques codificados en los sets elegidos.',
    emptyPlayer: 'El #{player} no tiene ataques codificados en los sets elegidos.',
    emptyTeamServe: 'Sin saques codificados en los sets elegidos.',
    emptyPlayerServe: 'El #{player} no tiene saques codificados en los sets elegidos.',
    emptyTeamReception: 'Sin recepciones codificadas en los sets elegidos.',
    emptyPlayerReception: 'El #{player} no tiene recepciones codificadas en los sets elegidos.',
    attacks: '{count} ataques',
    serves: '{count} saques',
    receptions: '{count} recepciones',
    kills: '{count} puntos',
    aces: '{count} aces',
    perfectReceptions: '{count} perfectas',
    killPct: 'Punto {pct}',
    acePct: 'Ace {pct}',
    positivityPct: 'Positividad {pct}',
    errors: '{count} errores',
    blocked: '{count} bloqueados',
    efficiency: 'Eficiencia {pct}',
    cellTitle:
      'Salida {startZone} → llegada {endZone}: {total} ataques, {kills} puntos, {errors} errores, {blocked} bloqueados · eficiencia {efficiency}',
    cellTitleServe:
      'Saque desde {startZone} → zona {endZone}: {total} saques, {kills} aces, {errors} errores, {blocked} rebotados · eficiencia {efficiency}',
    cellTitleReception:
      'Saque desde {startZone} → recibido en {endZone}: {total} recepciones, {kills} perfectas, {errors} errores, {blocked} rebotadas · eficiencia {efficiency}',
    noZoneData: 's/d',
    emptyMatrix: 'Sin toques con zona codificada.',
    axisRow: 'Salida',
    axisCol: 'Llegada',
    axisRowServe: 'Saque desde',
    axisColServe: 'Hacia',
    axisRowReception: 'Saque desde',
    axisColReception: 'Recibido en',
    matrixLabel: 'Matriz {rowLabel} × {colLabel}',
    legend: 'Eficiencia',
  },

  evolution: {
    empty: 'Todavía no hay puntos en el log para dibujar la evolución.',
    emptySet: 'El set {set} no tiene puntos cargados.',
    chartLabel: 'Evolución del set {set}',
    chartTitle: 'Set {set}: {homeName} {home} – {awayName} {away}',
    firstRally: 'rally 1',
    lastRally: 'rally {count}',
    barChartLabel: 'Gráfico de barras',
    distributionLabel: 'Distribución',
  },

  live: {
    statusIdle: 'Sin conectar',
    statusConnecting: 'Conectando…',
    statusOpen: 'En vivo',
    statusReconnecting: 'Reconectando…',
    statusClosed: 'Desconectado',
    statusError: 'Error de conexión',
    sets: 'Sets {home}–{away}',
    setClosed: 'set cerrado',
    lastEvents: 'Últimos eventos',
    empty:
      'Todavía no entró ningún evento. Si el partido está en juego, el capturador tiene que estar sincronizando contra este API.',
    openFailed: 'El API respondió {status} al abrir el vivo.',
    noFetch: 'Este entorno no tiene fetch.',
    noStreamBody: 'El navegador no expone el cuerpo del stream.',
  },

  /**
   * CONSOLA DE PARTIDO (Fase D). Los textos de acá se leen A UN METRO, así que
   * son cortos y dicen una cosa cada uno. Dos que no son de UI sino de
   * reglamento, y por eso están redactados con cuidado:
   *  · `link.keepPlaying` — es la promesa de §5.3 puesta en pantalla;
   *  · `request.useSignal` — cuando no hay red, el pedido oficial vuelve a ser
   *    el físico. No es una disculpa: es lo que dice la regla.
   */
  bench: {
    views: {
      banco: 'Banco',
      mesa: 'Mesa',
      arbitro: 'Árbitro',
      espejo: 'Supervisor',
      tablero: 'Tablero',
    },
    access: {
      title: 'Consola de partido',
      explain: 'Escaneá el QR que muestra la mesa para entrar a este partido.',
      hint: 'La credencial vale sólo para ese partido y con el rol que te asignaron.',
      loading: 'Cargando el partido…',
      failed: 'La credencial no se pudo canjear. Pedile a la mesa que emita una nueva.',
    },
    link: {
      offline: 'SIN RED',
      cold: 'SIN DATOS',
      keepPlaying: 'El partido sigue. Sin tablet, el pedido oficial es la seña.',
      lastData: 'último dato hace {seconds} s',
      retry: 'Reintentar',
    },
    prefs: {
      glareOn: 'Contraste alto',
      glareOff: 'Contraste normal',
    },
    score: {
      serving: 'Saca',
      servingShort: 'SAQ',
      closed: 'ACTA CERRADA',
      timeoutsLeft: 'tiempos',
      subsLeft: 'cambios',
      challengesLeft: 'challenges',
      timeoutsLeft2: 'quedan {n}',
      challengesLeft2: 'quedan {n}',
      exceptional: '{count} excepcional(es)',
    },
    court: {
      title: 'Cancha · {team}',
      flip: 'Girar cancha',
      unflip: 'Volver',
      noLineup: 'Todavía no hay formación cargada para este set.',
      net: 'RED',
    },
    rallies: {
      title: 'Rallies del set {set}',
      empty: 'Todavía no se jugó ningún punto.',
      servedBy: 'saque de {team}',
      unknownServer: 'saque sin registrar',
      jump: 'Ver {tc}',
      noTimecode: 'sin video',
    },
    numbers: {
      title: 'Números del partido',
      loading: 'Calculando…',
      unavailable: 'Los números no están disponibles con esta credencial.',
      sideout: 'Sideout',
      break: 'Break',
      attack: 'Ataque',
      reception: 'Recepción +',
      topScorers: 'Puntos',
    },
    request: {
      title: 'Pedir',
      timeout: 'Tiempo',
      challenge: 'Challenge',
      substitution: 'Cambio',
      sending: 'Enviando…',
      noNetwork: 'No se pudo enviar',
      useSignal: 'Pedilo con la seña al 2do árbitro: sin red, el pedido oficial es el físico.',
      underReview: 'Hay una revisión en curso: no se piden tiempos ni cambios.',
      subsUsed: '{used} de {limit}',
      subsWarn: 'Te quedan pocos cambios en este set.',
      noSubsLeft:
        'Sin cambios en este set. La sustitución excepcional (regla 15.7) se pide con la seña y la autoriza el 2do árbitro.',
      whoLeaves: '¿Quién sale?',
      whoEnters: '¿Quién entra?',
      cannotLeave: 'Ya salió y volvió en este set.',
      pairLocked: 'Sólo puede entrar el #{player}: es el que salió por él (regla 15.6).',
      noCandidates: 'No hay nadie disponible en el banco.',
    },
    lineup: {
      title: 'Formación del set {set}',
      official: 'La formación mandada por tablet es oficial (regla 5.2.3.1).',
      send: 'Enviar formación',
      clear: 'Borrar',
      useSheet: 'Sin red: entregá la ficha de posiciones en papel.',
    },
    queue: {
      title: 'Pedidos',
      empty: 'No hay pedidos.',
      pending: '{n} sin atender',
      ago: 'hace {seconds} s',
      by: 'pidió {who}',
      accept: 'Aceptar',
      reject: 'Rechazar',
      cancel: 'Retirar',
      accepted: 'Aceptado por {who}',
      rejected: 'Rechazado: {reason}',
      cancelled: 'Retirado por el banco.',
      stale: 'Venció: el juego siguió. Si corresponde, cargalo a mano.',
      subDetail: 'sale #{out}, entra #{in}',
      kinds: {
        timeout: 'Tiempo',
        substitution: 'Cambio',
        challenge: 'Challenge',
        lineup: 'Formación',
      },
    },
  },

  /**
   * MESA DE CONTROL (`#/mesa/:matchId`): la pantalla del anotador en la
   * laptop. Acreditar (Oficiales), resolver el challenge y firmar/cerrar el
   * acta. Es la pantalla que se opera bajo presión: los textos dicen QUÉ va a
   * pasar antes de que pase, y las citas de reglamento van donde la decisión
   * duele (INVERT cambia el saque; el anotador firma primero).
   */
  mesa: {
    title: 'Mesa de control',
    tabs: {
      oficiales: 'Oficiales',
      challenge: 'Challenge',
      acta: 'Acta',
    },
    loadingWhat: 'el acta',
    closedBanner:
      'El acta está cerrada y firmada: lo que llegue ahora necesita una rectificación autorizada.',
    phase: {
      BEFORE_MATCH: 'Antes del partido',
      AFTER_TOSS: 'Sorteo hecho',
      DURING_MATCH: 'Partido en curso',
      AFTER_MATCH: 'Partido terminado',
      CLOSED: 'Acta cerrada',
    },
    team: {
      home: 'local',
      away: 'visitante',
    },
    roles: {
      referee1: '1er árbitro',
      referee2: '2do árbitro',
      challenge_referee: 'Árbitro de challenge',
      reserve_referee: 'Árbitro de reserva',
      scorer: 'Anotador',
      assistant_scorer: 'Anotador asistente',
      line_judge: 'Juez de línea',
      team_delegate: 'Delegado de banco',
      team_captain: 'Capitán',
      supervisor: 'Supervisor',
      replay_operator: 'Operador de replay',
      scoreboard: 'Pantalla de estadio',
      statistician: 'Estadístico',
      system: 'Sistema',
    },
    oficiales: {
      title: 'Cuerpo arbitral',
      explain:
        'Lo que se carga acá va al acta (caja APPROVAL) y define quién puede firmar qué.',
      empty: 'Todavía no se cargó el equipo arbitral.',
      role: 'Rol',
      name: 'Nombre',
      country: 'País (ISO3)',
      license: 'Matrícula',
      add: 'Agregar oficial',
      remove: 'Quitar',
      save: 'Registrar en el acta',
      saved: 'Cuerpo arbitral registrado en el acta.',
      nameMissing: 'Cada oficial necesita nombre.',
      credsTitle: 'Credenciales del partido (QR+PIN)',
      credsExplain:
        'Una por persona presente. El link abre la consola con su rol; el PIN se dicta al titular y es lo que firma: el link sin PIN sirve para operar, no para firmar.',
      credsEmpty: 'Todavía no se emitió ninguna credencial.',
      holder: 'Titular',
      side: 'Banco',
      sideNone: 'Sin banco',
      issue: 'Emitir credencial',
      holderMissing: 'Falta el nombre del titular.',
      revoke: 'Revocar',
      revoked: 'Revocada',
      confirmRevoke: '¿Revocar la credencial de {holder}? Deja de autenticar al instante.',
      kindAccount: 'cuenta del padrón',
      kindCredential: 'QR+PIN',
      issuedTitle: 'Credencial emitida — esto se muestra UNA sola vez',
      issuedLink: 'Link de acceso (es el contenido del QR)',
      issuedPin: 'PIN — dictáselo al titular, no lo dejes a la vista',
      copyLink: 'Copiar link',
      copied: 'Copiado.',
      qrPending:
        'Que el titular lo escanee con la cámara de su tablet: entra directo a la consola con su rol. Si no hay cámara, el link también se puede copiar o tipear.',
      issuedDone: 'Listo, lo entregué',
    },
    challenge: {
      title: 'Challenge en curso',
      none: 'No hay ninguna revisión en curso.',
      windowOpen: 'La ventana del último punto está abierta: todavía se puede desafiar (art. 7).',
      remainingNow: 'Challenges restantes — {home} el local · {away} el visitante',
      requestedBy: 'Desafía el equipo {team}',
      requestedByRef: 'Revisión de oficio del 1er árbitro (art. 12)',
      status: {
        AWAITING_REASON: 'Esperando el motivo',
        UNDER_REVIEW: 'En revisión',
      },
      setScore: 'Set {set} · {score} (la cifra del solicitante primero)',
      at: 'Pedido a los {tc} del video',
      claimed: 'Motivo declarado: {fault}',
      claimedNone: 'Sin motivo declarado (art. 8: se elige en un segundo paso)',
      pickFault: 'Registrar el motivo que eligió el banco',
      faults: {
        BALL_IN_OUT: 'Pelota adentro/afuera',
        BLOCK_TOUCH: 'Toque de bloqueo',
        NET_FAULT: 'Falta de red',
        ANTENNA_TOUCH: 'Toque de antena',
        FOOT_FAULT: 'Falta de pie',
        FLOOR_TOUCH: 'Toque del piso',
        LAST_TOUCH: 'Último toque',
        REACH_OVER: 'Invasión sobre la red',
      },
      resolveTitle: 'Resultado de la revisión',
      onlyUnsuccessful:
        'El pedido nunca declaró la falta: la única resolución posible es «incorrecto» (art. 24).',
      outcomes: {
        SUCCESSFUL: 'Correcto',
        UNSUCCESSFUL: 'Incorrecto',
        INCONCLUSIVE: 'Inconcluyente',
        DECLINED_UNNECESSARY: 'Innecesario',
        FOOTAGE_UNAVAILABLE: 'Sin video',
      },
      outcomeHints: {
        SUCCESSFUL: 'El reclamo era cierto. Retiene el challenge (art. 2).',
        UNSUCCESSFUL: 'El reclamo era falso. PIERDE el challenge (art. 11).',
        INCONCLUSIVE: 'El video no alcanza para decidir. Retiene el challenge (art. 2).',
        DECLINED_UNNECESSARY: 'Se cayó solo: ganó el rally igual o el rival admitió (art. 14). Retiene.',
        FOOTAGE_UNAVAILABLE: 'No hubo imagen que revisar. Retiene el challenge.',
      },
      retained: 'El equipo RETIENE el challenge.',
      lost: 'El equipo PIERDE el challenge.',
      observed: 'Falta observada (prevalece la primera observada, aunque no sea la desafiada — art. 13)',
      report: 'Informe del Challenge Referee (opcional)',
      scoreTitle: 'Reconciliación del marcador',
      scoreExplain:
        'No es un deshacer genérico: INVERT cambia el equipo al saque y DELETE se lo devuelve al mismo sacador. Equivocarse acá rompe la rotación del resto del set.',
      actions: {
        CONFIRM: 'CONFIRMAR',
        INVERT: 'INVERTIR el punto',
        DELETE_LAST_POINT: 'ANULAR el último punto',
        REPLAY: 'REPETIR la jugada',
      },
      actionHints: {
        CONFIRM: 'El marcador queda como está.',
        INVERT: 'El punto pasa al otro equipo: cambia quién saca.',
        DELETE_LAST_POINT: 'Se anula el punto: el saque vuelve al mismo sacador.',
        REPLAY: 'Se anula el punto y la jugada se repite: saca el mismo equipo.',
      },
      planConfirm: 'El marcador no cambia.',
      planInvert: 'Se invierte el último punto: pasa al equipo {team}.',
      planDelete: 'Se anula el último punto: el saque vuelve al mismo sacador.',
      planReplay: 'Se anula el punto y se repite la jugada.',
      afterRemaining: 'Después de esto quedan: {home} al local · {away} al visitante.',
      noPoint: 'El log no tiene un punto vigente para reconciliar.',
      confirm: 'Registrar la resolución',
      resolvedOk:
        'Challenge resuelto y marcador reconciliado en un solo lote. Checkpoint del 2do árbitro: acta, marcador y rotación antes de habilitar el saque.',
      reviewSeconds: 'Revisión en curso: {seconds} s (sin tope reglamentario)',
      logTitle: 'Challenges del partido',
      logEmpty: 'Ninguno todavía.',
      logRetained: 'retenido',
      logLost: 'perdido',
    },
    acta: {
      title: 'Firmas y cierre',
      rosterTitle: 'Verificación de roster (regla 4.1.3)',
      rosterExplain:
        'Con las firmas previas de cada banco, el plantel de ese equipo queda congelado: después de esto no entra nadie más a la lista.',
      rosterLocked: 'Plantel CONGELADO',
      rosterOpen: 'Plantel todavía abierto',
      players: '{count} jugadores en lista',
      signaturesTitle: 'Firmas del cierre — instrucción 4.10, en este orden',
      scorerFirst:
        'El anotador firma primero: su firma congela el digest y las demás firman ESE documento (regla 27.2.3.3).',
      signedBy: 'Firmó {name}',
      pendingSlot: 'Falta firmar',
      nextBadge: 'FIRMA SIGUIENTE',
      noHolder: 'Sin acreditar: emití su credencial en Oficiales.',
      digestMismatch: '¡Esta firma NO coincide con el digest congelado! El acta cambió entre firmas.',
      sign: 'Firmar',
      signAs: 'Firma {name} ({role})',
      secretPin: 'PIN de su credencial — lo ingresa el titular, no la mesa',
      secretPassword: 'Contraseña de su cuenta — la ingresa el titular, no la mesa',
      needsToken:
        'Esta credencial se emitió en otro navegador y la mesa no guarda su token: reemitila en Oficiales o que firme desde su propio dispositivo.',
      accountHint:
        'Cuenta del padrón: firma con su contraseña. Vale si la sesión activa de esta mesa es la suya, o desde su propia sesión.',
      signedOk: 'Firma registrada y atada al digest del documento.',
      frozenDigest: 'Digest congelado: {digest}',
      closeTitle: 'Cierre y entrega',
      closeExplain:
        'El cierre congela el documento que se firmó: después de esto el motor rechaza cualquier agregado sin una rectificación autorizada (§8.4).',
      outcomeLabel: 'Resultado del partido',
      outcomes: {
        NORMAL: 'Normal',
        DEFAULT: 'Ausente (0-3, regla 6.4)',
        INCOMPLETE: 'Equipo incompleto (conserva sus puntos)',
        WITHDRAWN: 'Retirado',
      },
      deliveriesLabel: 'Entrega del documento firmado',
      deliveries: {
        FEDERATION: 'Federación',
        TEAM_A: 'Equipo A',
        TEAM_B: 'Equipo B',
        ORGANISER: 'Comité organizador',
        SUPERVISOR: 'Supervisor',
      },
      close: 'CERRAR EL ACTA',
      confirmClose:
        '¿Cerrar el acta? Congela el documento firmado: después sólo se toca con una rectificación autorizada.',
      closeUnsigned: 'Sin ninguna firma de cierre no se puede cerrar: firma primero el anotador.',
      closedOk: 'Acta cerrada y entregada.',
      closedDigest: 'Digest del documento cerrado: {digest}',
      printable: 'Abrir la planilla',
    },
  },

  season: {
    title: 'Temporada',
    includedMatches: 'Partidos incluidos',
    apply: 'Aplicar',
    noMatches: 'No hay partidos en el archivo todavía.',
    loadingWhat: 'partidos',
    loadingTotals: 'el acumulado',
    allSeason: 'Sin selección = toda la temporada.',
    selectedCount: '{count} partido(s) seleccionado(s).',
    totals: 'Acumulado · {count} partido(s)',
    setsChip: 'Sets: {sets}',
    setsAll: 'todos',
    colTeam: 'Equipo',
    colMatches: 'PJ',
    colSideout: 'Sideout%',
    colBreak: 'Break%',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies recibidos',
    colServed: 'Saq',
    colServedTitle: 'Rallies sacados',
    topEfficiency: 'Top eficiencia de ataque',
    minAttacks: 'Mínimo {count} ataques en el acumulado.',
    topPoints: 'Top puntos',
    unitAttacks: 'ataques',
    unitMatches: 'PJ',
    bySkill: 'Por fundamento',
    players: 'Jugadores',
    noRanked: 'Sin jugadores que califiquen todavía.',
    colValue: 'Valor',
  },

  /** Rotation Planner (#/planner): la pizarra de rotaciones. */
  planner: {
    title: 'Rotation Planner',
    intro:
      'Sexteto inicial → las 6 rotaciones: quién queda en cada zona, qué solapamientos obliga la regla y cómo rindió cada rotación si hay datos.',
    sourceCard: 'Datos del partido (opcional)',
    sourceIntro:
      'Elegí un partido para precargar el sexteto titular, ponerle nombres a las camisetas y cruzar cada rotación con su estadística real.',
    matchLabel: 'Partido',
    noMatch: 'Sin partido (pizarra vacía)',
    teamLabel: 'Equipo',
    prefill: 'Cargar sexteto titular',
    seasonToggle: 'Acumular toda la temporada del equipo',
    noEvents:
      'Tu rol no puede leer el log de eventos, así que no hay precarga ni estadística por rotación.',
    noEventsHint: 'La pizarra sigue funcionando: tipeá las camisetas a mano.',
    loadingSeason: 'la temporada del equipo',
    seasonEmpty:
      'Ningún partido de la temporada tiene rotación reconstruible para este equipo (falta lineup o armador identificable).',
    lineupCard: 'Sexteto inicial',
    lineupIntro:
      'Una camiseta por posición. P1 es el zaguero derecho (el que saca); P2–P3–P4 son la red.',
    slotLabel: 'Camiseta en P{position}',
    setterLabel: 'Armador',
    noSetter: 'Sin armador',
    duplicates: 'Camisetas repetidas: {numbers}. Así ninguna rotación es legal.',
    incomplete: 'El sexteto está incompleto: las rotaciones se dibujan igual, con huecos.',
    noSetterHint:
      'Sin armador elegido no se numeran las rotaciones (P1..P6 es la posición del armador) ni se cruzan con la estadística.',
    empty: 'Tipeá el sexteto (o precargalo de un partido) y las 6 rotaciones aparecen acá.',
    untracked:
      'Este equipo no tiene rotación reconstruible en el partido elegido (sin lineup o sin armador identificable): las tarjetas salen sin estadística.',
    rotationTitle: 'Rotación {order}',
    rotationTitleSetter: 'Rotación {order} — armador en P{position}',
    ralliesTitle: 'Rallies jugados con esta rotación en los datos elegidos.',
    rallies: '{count} rallies',
    sideoutTitle: 'Puntos ganados recibiendo / rallies recibidos, con esta rotación.',
    sideout: 'SO {pct}',
    breakTitle: 'Puntos ganados sacando / rallies sacados, con esta rotación.',
    breakPoint: 'BP {pct}',
    netTitle: 'Puntos ganados menos perdidos con esta rotación.',
    setterChip: 'A',
    overlapsTitle: 'Solapamientos a cuidar (regla 7.4)',
    overlapFrontBack:
      '{back} (Z{backZone}) no puede adelantarse a {front} (Z{frontZone}) hasta el saque.',
    overlapLeftRight:
      '{left} (Z{leftZone}) tiene que quedar a la izquierda de {right} (Z{rightZone}) hasta el saque.',
    emptySlot: 'hueco',
  },

  competition: {
    // --- Listado -------------------------------------------------------------
    title: 'Competencias',
    subtitle: 'Torneos, fixture y tabla de posiciones de la federación.',
    loadingWhat: 'competencias',
    empty:
      'No hay competencias cargadas todavía. Una competencia agrupa fases (regular, grupos, playoff), su fixture y su tabla — y engancha los partidos que ya están en el archivo.',
    colName: 'Competencia',
    colSeason: 'Temporada',
    colStatus: 'Estado',
    colTeams: 'Equipos',
    colPhases: 'Fases',
    colUpdated: 'Actualizada',
    open: 'Abrir',
    statusDraft: 'Borrador',
    statusActive: 'En curso',
    statusFinished: 'Terminada',

    // --- Ficha ---------------------------------------------------------------
    tabFixture: 'Fixture',
    tabStandings: 'Posiciones',
    tabInfo: 'Ficha',
    notFound: 'No existe esa competencia.',
    backToList: 'Volver a competencias',
    loadingCompetition: 'la competencia',
    phase: 'Fase',
    group: 'Grupo',
    allPhases: 'Todas las fases',
    teamsTitle: 'Equipos participantes',
    phasesTitle: 'Fases',
    phaseKindRegular: 'Todos contra todos',
    phaseKindGroups: 'Grupos',
    phaseKindPlayoff: 'Eliminación directa',
    scoringTitle: 'Sistema de puntos',
    scoringIntro:
      'Cada competencia reparte los puntos a su manera; el sistema es dato, no está cableado.',
    scoringRule: '{winner}-{loser} → {winnerPoints} y {loserPoints}',
    scoringFallback: 'Cualquier otro resultado: {winnerPoints} al ganador y {loserPoints} al perdedor.',
    scoringForfeit: 'W.O.: {sets} con {points} por set.',
    tiebreakTitle: 'Criterios de desempate, en orden',
    legs: 'Vueltas',
    legsOne: 'Sólo ida',
    legsTwo: 'Ida y vuelta',

    // --- Fixture -------------------------------------------------------------
    matchday: 'Fecha {matchday}',
    fixtureEmpty:
      'Esta competencia todavía no tiene fixture. Se puede generar desde una fase o cargar los partidos a mano.',
    generate: 'Generar fixture',
    generating: 'Generando…',
    generateHint:
      'Genera el calendario de la fase elegida. Regenerar NO desengancha los partidos ya jugados.',
    generateDone: 'Fixture generado: {count} partido(s).',
    thirdPlace: 'Con partido por el tercer puesto',
    vs: 'vs',
    noRival: 'A definir',
    winnerOf: 'Ganador de {of}',
    loserOf: 'Perdedor de {of}',
    roundFinal: 'Final',
    roundSemi: 'Semifinal',
    roundQuarter: 'Cuartos de final',
    roundOf16: 'Octavos de final',
    roundNumber: 'Ronda {round}',
    matchNumber: '{round} {position}',
    thirdPlaceMatch: 'Tercer puesto',
    statusScheduled: 'Programado',
    statusLive: 'En juego',
    statusPlayed: 'Jugado',
    statusWalkover: 'W.O.',
    statusPostponed: 'Postergado',
    statusCancelled: 'Suspendido',
    originLog: 'del scouting',
    originManual: 'cargado a mano',
    originWalkover: 'por reglamento',
    resultFrom: 'Resultado {origin}',
    openMatch: 'Ver el partido',
    venue: 'Sede',
    colDate: 'Día',
    colMatch: 'Partido',
    colResult: 'Resultado',
    colSets: 'Parciales',
    colStatusFixture: 'Estado',

    // --- Posiciones ----------------------------------------------------------
    standingsEmpty: 'Todavía no hay fases con tabla en esta competencia.',
    standingsIntro:
      'La tabla se calcula de los partidos cargados: no se sube a mano. Sólo suman los jugados y los W.O.',
    computedWith: 'Calculada con {scoring}',
    matchesCounted: '{counted} de {total} partido(s) con resultado',
    colPos: '#',
    colTeam: 'Equipo',
    colPlayed: 'PJ',
    colWon: 'PG',
    colLost: 'PP',
    colSetsWon: 'SF',
    colSetsLost: 'SC',
    colSetRatio: 'Coc. sets',
    colPointsFor: 'PF',
    colPointsAgainst: 'PC',
    colPointRatio: 'Coc. puntos',
    colTablePoints: 'Pts',
    colAdjustment: 'Ajuste',
    tied: 'Empatado con el de arriba',
    brokenBy: 'Desempata: {criterion}',
    adjustmentTitle: 'Ajuste de puntos: {points} ({reason})',
    tbPoints: 'puntos',
    tbMatchesWon: 'partidos ganados',
    tbSetRatio: 'cociente de sets',
    tbSetDiff: 'diferencia de sets',
    tbPointRatio: 'cociente de puntos',
    tbPointDiff: 'diferencia de puntos',
    tbHeadToHead: 'resultado entre ellos',
    tbSetsWon: 'sets ganados',
    tbPointsScored: 'puntos anotados',

    // --- Enganche de partidos ------------------------------------------------
    linkTitle: 'Enganchar partidos del archivo',
    linkHint:
      'Los partidos que ya están cargados y no pertenecen a ninguna fecha. Al engancharlos, el resultado sale del log: la tabla se actualiza sola.',
    linkEmpty: 'No quedan partidos sueltos para enganchar.',
    linkAction: 'Enganchar {count} partido(s)',
    linkFilter: 'Filtrar por la competencia declarada en el partido',
    linkDone: '{linked} partido(s) enganchado(s); {teams} equipo(s) dados de alta.',
    linkSkipped: '{count} partido(s) no se pudieron enganchar.',
    selectAll: 'Seleccionar todos',
    clearSelection: 'Limpiar selección',
  },

  /**
   * ABM de competencias: el alta y la edición de la ficha (fases, grupos,
   * sistema de puntos, desempates). Bloque aparte del de `competition`, que es
   * lo que se MUESTRA; acá está lo que se CARGA.
   */
  competitionForm: {
    // --- Acciones y avisos ---------------------------------------------------
    newCompetition: 'Nueva competencia',
    edit: 'Editar',
    remove: 'Borrar',
    editing: 'Editando {id}',
    savedNotice: 'Competencia «{name}» guardada.',
    deletedNotice: 'Competencia «{name}» borrada.',
    confirmDelete:
      '¿Borrar la competencia «{name}»? Se lleva el fixture; los partidos ya cargados quedan en el archivo.',
    noPermission:
      'Para crear o editar competencias hace falta el permiso {permission}, que hoy tiene el rol administrador.',

    // --- Identidad -----------------------------------------------------------
    identityTitle: 'Datos de la competencia',
    competitionId: 'Id',
    competitionIdHint: 'Va en la URL y engancha el fixture. No se puede cambiar después.',
    competitionIdPlaceholder: 'liga-a1-2026',
    name: 'Nombre',
    namePlaceholder: 'Liga A1',
    season: 'Temporada',
    seasonPlaceholder: '2026 o 2025/26',
    status: 'Estado',
    sportConfig: 'Reglamento',
    description: 'Descripción',

    // --- Equipos -------------------------------------------------------------
    teamsTitle: 'Equipos participantes',
    teamsHint:
      'Un club por línea: «id, Nombre, siembra». Con una sola columna se toma como nombre y el id se propone solo. La siembra define los cruces de la eliminación directa.',
    teamsPlaceholder: 'bolivar, Club Bolívar, 1\nupcn, UPCN Vóley, 2\nCiudad Vóley',
    teamsCount: '{count} equipo(s) cargado(s).',

    // --- Sistema de puntos ---------------------------------------------------
    scoringTitle: 'Sistema de puntos',
    scoringHint:
      'Cómo reparte puntos cada resultado. Es dato de la competencia, no está cableado: si esta liga da 4 puntos por un 3-1, es una línea más.',
    preset: 'Preset',
    presetCustom: 'Personalizado',
    presetFivb: 'FIVB (3-0 y 3-1 → 3; 3-2 → 2 y 1)',
    presetFivbBo3: 'Al mejor de 3 (2-0 → 3; 2-1 → 2 y 1)',
    presetWinLoss21: '2 puntos por partido ganado, 1 por perdido',
    presetWin10: '1 punto por partido ganado',
    scoringLabel: 'Etiqueta',
    rules: 'Tabla de puntos',
    rulesHint:
      'Una línea por resultado, siempre desde la perspectiva del ganador: «3-2 2 1» = el que gana 3-2 suma 2 y el que pierde 1.',
    rulesPlaceholder: '3-0 3 0\n3-1 3 0\n3-2 2 1',
    winPoints: 'Puntos del ganador',
    lossPoints: 'Puntos del perdedor',
    fallbackHint:
      'Se usan para los resultados que la tabla no enumera (un partido a 3 sets en una competencia declarada al mejor de 5, por ejemplo).',
    forfeitTitle: 'W.O. / partido no presentado',
    forfeitSets: 'Sets del ganador',
    forfeitLoserSets: 'Sets del perdedor',
    forfeitPointsPerSet: 'Parcial por set',
    forfeitWinnerPoints: 'Puntos del ganador',
    forfeitWinnerPointsHint: 'Vacío = lo que diga la tabla para ese resultado.',
    forfeitLoserPoints: 'Puntos del perdedor',

    // --- Desempates ----------------------------------------------------------
    tiebreakTitle: 'Criterios de desempate',
    tiebreakHint:
      'El ORDEN importa tanto como la lista: dos ligas con los mismos criterios en distinto orden arman tablas distintas.',
    tiebreakAdd: 'Agregar criterio',
    tiebreakUp: 'Subir',
    tiebreakDown: 'Bajar',
    tiebreakRemove: 'Quitar',
    tiebreakRestore: 'Volver al orden FIVB',
    tiebreakEmptyHint: 'Sin criterios la tabla no sabe cómo ordenar a dos equipos empatados.',

    // --- Fases ---------------------------------------------------------------
    phasesTitle: 'Fases',
    phasesHint:
      'Una competencia es una o más fases: regular, grupos o eliminación directa. El fixture se genera por fase, desde la ficha.',
    phasesEmpty: 'Todavía no hay fases. Sin al menos una no se puede generar el fixture.',
    addPhase: 'Agregar fase',
    removePhase: 'Quitar fase',
    phaseNumber: 'Fase {number}',
    phaseId: 'Id de la fase',
    phaseName: 'Nombre',
    phaseNamePlaceholder: 'Fase regular',
    phaseKind: 'Tipo',
    phaseLegs: 'Vueltas',
    phaseTeams: 'Equipos de la fase',
    phaseTeamsHint:
      'Ids separados por coma. Vacío = todos los de la competencia, ordenados por siembra.',
    phaseGroups: 'Grupos',
    phaseGroupsHint: 'Uno por línea: «Grupo A: bolivar, upcn». Sólo en fase de grupos.',
    phasePreserved:
      'Esta fase tiene configuración cargada por API que esta pantalla no edita ({detail}). Se conserva tal cual al guardar.',
    phasePreservedScoring: 'sistema de puntos propio',
    phasePreservedTiebreakers: 'desempates propios',
    phasePreservedAdjustments: '{count} ajuste(s) de puntos',

    // --- Errores -------------------------------------------------------------
    errors: {
      idRequired: 'Falta el id de la competencia.',
      idInvalid: 'El id solo admite letras, números, punto, guión y guión bajo.',
      nameRequired: 'Falta el nombre de la competencia.',
      seasonTooLong: 'La temporada no puede pasar de 40 caracteres.',
      descriptionTooLong: 'La descripción no puede pasar de 1000 caracteres.',
      number: 'El campo «{field}» tiene un valor inválido.',
      teamId: 'Línea {line} de equipos: falta el id.',
      teamIdInvalid:
        'Línea {line} de equipos: el id «{id}» solo admite letras, números, punto, guión y guión bajo.',
      teamName: 'Línea {line} de equipos: falta el nombre del club.',
      teamRepeated: 'Línea {line} de equipos: el id «{id}» está dos veces.',
      teamSeed: 'Línea {line} de equipos: «{token}» no es una siembra válida (1 a 256).',
      ruleShape:
        'Línea {line} de la tabla de puntos: se esperan cuatro números (sets del ganador, del perdedor, puntos de cada uno).',
      ruleSets: 'Línea {line} de la tabla de puntos: los sets tienen que ser enteros de 0 a 9.',
      ruleWinner:
        'Línea {line} de la tabla de puntos: la fila se escribe desde el ganador, así que sus sets tienen que ser más.',
      rulePoints: 'Línea {line} de la tabla de puntos: los puntos no pueden pasar de 99.',
      ruleRepeated: 'Línea {line} de la tabla de puntos: el resultado {result} ya estaba.',
      groupName: 'Línea {line} de grupos: falta el nombre del grupo.',
      groupId: 'Línea {line} de grupos: de «{name}» no sale un id válido.',
      groupRepeated: 'Línea {line} de grupos: «{name}» está dos veces.',
      phaseId: 'Fase {phase}: falta el id.',
      phaseIdInvalid: 'Fase {phase}: el id solo admite letras, números, punto, guión y guión bajo.',
      phaseName: 'Fase {id}: falta el nombre.',
      phaseRepeated: 'La fase «{id}» está dos veces.',
      phaseUnknownTeam:
        'Fase {phase}: usa el equipo «{id}», que no está en la lista de participantes.',
      tiebreakEmpty: 'Hace falta al menos un criterio de desempate.',
    },
  },

  /**
   * Import de `.dvw` histórico (ítem 2.7 del plan). El vocabulario es el de la
   * federación: "archivo", "temporada", "partido", nunca "log" ni "evento
   * canónico" salvo donde el número es el que importa.
   */
  import: {
    title: 'Importar histórico (.dvw)',
    navLabel: 'Importar',
    intro:
      'Elegí los .dvw de una temporada entera: se analizan primero y no se guarda nada hasta que aprietes Importar. Los archivos de DataVolley entran con planteles, alineaciones, rallies y sanciones; lo que el formato no permita traducir se muestra abajo, línea por línea.',
    fileLabel: 'Archivos .dvw (se pueden elegir todos juntos)',
    chosen: '{count} archivo(s) elegidos',
    notDvw: '{count} no termina(n) en .dvw: se van a intentar igual.',
    noPermission: 'No podés importar histórico con este rol.',
    noPermissionHint:
      'El API exige el permiso {permission}, que tienen la dirigencia y el estadístico. La pantalla se ve igual para que sepas qué pedir.',
    noTeamsPermission: 'Los partidos van a entrar, pero la base de equipos no se toca.',
    noTeamsPermissionHint:
      'Poblar planteles pide {permission} (dirigencia). Sin ese permiso el import carga los partidos y deja los equipos como están, en vez de hacerlo a medias sin avisar.',
    readingBatch: 'Analizando tanda {batch} de {total}…',
    writingBatch: 'Importando tanda {batch} de {total}…',
    previewCard: 'Lo que se va a importar',
    resultCard: 'Lo que se importó',
    willImport: 'Van a entrar {matches} partido(s) nuevos, con {events} eventos.',
    didImport: 'Entraron {matches} partido(s), con {events} eventos.',
    alreadyImported: '{count} ya estaban importados: no se tocan (el mismo archivo da siempre el mismo partido).',
    failedFiles: '{count} archivo(s) no se pudieron leer.',
    unmappedTotal: '{count} línea(s) quedaron como comentario en vez de como evento: están abajo.',
    teamsResult: 'Equipos: {created} creados, {merged} actualizados con jugadores nuevos.',
    importButton: 'Importar {count} partido(s)',
    nothingToImport: 'No hay nada nuevo para importar.',
    previewOnly: 'Hasta acá no se escribió nada.',
    goToMatches: 'Ver los partidos importados',
    unmappedCard: 'Lo que no se pudo traducir',
    unmappedIntro:
      'El importador no descarta nada: lo que el modelo canónico no representa entra como comentario con su línea original, así que el archivo se puede volver a exportar sin agujeros. Esto es exactamente qué quedó así.',
    unmappedNone: 'Todo el archivo se tradujo a eventos: no quedó ninguna línea suelta.',
    kind_unknown_code: 'Código que el parser no reconoce (mirá el archivo)',
    kind_green_code: 'Punto sin detalle de acción (código verde de DataVolley)',
    kind_setter_change: 'Cambio de armador (el modelo no tiene ese evento)',
    kind_sanction_no_team: 'Tarjeta sin equipo (cuerpo arbitral)',
    teamsCard: 'Equipos que trae el histórico',
    teamsNone: 'Ningún archivo trae plantel.',
    teamState_created: 'se crea',
    teamState_merged: 'se le agregan jugadores',
    teamState_unchanged: 'sin cambios',
    teamState_skipped: 'no se toca (falta permiso)',
    filesCard: 'Archivo por archivo',
    colCode: 'Código',
    colWhy: 'Por qué',
    colLines: 'Líneas',
    colFiles: 'Archivos',
    colSample: 'Muestra',
    colTeam: 'Equipo',
    colNewPlayers: 'Jugadores nuevos',
    colFile: 'Archivo',
    colMatch: 'Partido',
    colDate: 'Fecha',
    colSets: 'Sets',
    colEvents: 'Eventos',
    colUnmapped: 'Sin traducir',
    colState: 'Estado',
    outcome_preview: 'listo para importar',
    outcome_imported: 'importado',
    outcome_already_imported: 'ya estaba',
    outcome_failed: 'no se pudo leer',
  },

  teams: {
    title: 'Equipos',
    newTeam: 'Nuevo equipo',
    editing: 'Editando {id}',
    loadingWhat: 'equipos',
    empty:
      'La base de equipos está vacía. Con «Nuevo equipo» se carga un plantel una sola vez y después la captura lo reutiliza en cada partido.',
    name: 'Nombre',
    namePlaceholder: 'Bolívar',
    teamId: 'Id (clave en el archivo)',
    teamIdPlaceholder: 'bolivar',
    code: 'Código (hasta 8)',
    codePlaceholder: 'BOL',
    coach: 'Director técnico',
    roster: 'Plantel — una línea por jugador: número, nombre y rol opcional ({roles})',
    rosterPlaceholder: '1 Pérez armador\n14 Palacios punta\n6 Gómez líbero',
    liberos: 'Líberos (camisetas, separadas por coma)',
    liberosPlaceholder: '6, 12',
    savedNotice: 'Equipo «{name}» guardado.',
    deletedNotice: 'Equipo «{name}» borrado.',
    confirmDelete: '¿Borrar el equipo «{name}»? No se puede deshacer.',
    colTeam: 'Equipo',
    colCode: 'Código',
    colCoach: 'DT',
    colPlayers: 'Jugadores',
    colLiberos: 'Líberos',
    colUpdated: 'Actualizado',
    colActions: 'Acciones',
    edit: 'Editar',
    remove: 'Borrar',
    roles: {
      setter: 'armador',
      outside: 'punta',
      opposite: 'opuesto',
      middle: 'central',
      libero: 'líbero',
      unknown: 'sin rol',
    },
    errors: {
      idRequired: 'Falta el id del equipo (es la clave en el archivo).',
      idInvalid: 'El id sólo admite letras, números, punto, guión y guión bajo.',
      nameRequired: 'Falta el nombre del equipo.',
      codeTooLong: 'El código no puede tener más de 8 caracteres.',
      badNumber: 'Línea {line}: «{token}» no es una camiseta válida (0 a 99).',
      repeatedNumber: 'Línea {line}: la camiseta #{number} está repetida.',
      badLibero: '«{token}» no es una camiseta válida de líbero.',
      liberoNotInRoster: 'El líbero #{number} no está en el plantel.',
    },
  },

  /**
   * Login y sesión. El portal distingue TRES formas de estar autenticado y las
   * nombra distinto en pantalla: sesión de usuario (lo normal), token de
   * compatibilidad (el Bearer de Ajustes) y API abierto (dev/LAN).
   */
  auth: {
    title: 'Ingresar',
    intro: 'Entrá con el usuario de la federación para ver el archivo con tu rol.',
    username: 'Usuario',
    usernamePlaceholder: 'nombre.apellido',
    password: 'Contraseña',
    submit: 'Ingresar',
    submitting: 'Ingresando…',
    logout: 'Salir',
    loggingOut: 'Saliendo…',
    missingFields: 'Completá usuario y contraseña.',
    badCredentials: 'Usuario o contraseña incorrectos.',
    accountDisabled: 'La cuenta está deshabilitada: pedile a un administrador que la reactive.',
    tooManyAttempts: 'Demasiados intentos fallidos. Esperá un minuto y probá de nuevo.',
    sessionExpired: 'Tu sesión venció o te la revocaron. Volvé a ingresar.',
    welcome: 'Entraste como {name}.',
    alreadyIn: 'Ya estás dentro como {name} ({role}).',
    goToMatches: 'Ir a Partidos',
    signedInAs: '{name} · {role}',
    since: 'Sesión iniciada el {date}.',
    anon: 'Sin sesión',
    anonTitle: 'Nadie inició sesión en este navegador',
    login: 'Ingresar',
    apiClosed:
      'El API de la federación pide credenciales. Ingresá con tu usuario o cargá el token de compatibilidad en Ajustes.',
    compatCard: 'Modo compatibilidad',
    compatHint:
      'Si en vez de usuario y contraseña tenés el token único de la federación, cargalo en Ajustes. Sigue funcionando para integraciones, pero no deja rastro de QUIÉN hizo cada cosa: la auditoría lo registra como «token de la federación».',
    compatLink: 'Ir a Ajustes',
    viaSession: 'sesión',
    viaLegacyToken: 'token de compatibilidad',
    viaOpen: 'API abierto (dev)',
    viaLegacyTokenTitle:
      'Estás usando el Bearer manual de Ajustes (modo compatibilidad): el API te trata como admin y la auditoría no sabe quién sos.',
    viaOpenTitle: 'El API está en modo dev abierto (SCOUT_DEV_OPEN=1): no pide credenciales.',
    viaMatchCredential: 'credencial de partido',
    viaMatchCredentialTitle:
      'Entraste con la credencial QR+PIN que emitió la mesa: vale sólo para este partido y vence sola.',
    roles: {
      admin: 'admin',
      scout: 'estadístico',
      coach: 'entrenador',
      viewer: 'dirigencia',
    },
  },

  /** Padrón de usuarios: el ABM que hasta ahora se hacía con curl. */
  users: {
    title: 'Usuarios',
    navLabel: 'Usuarios',
    intro:
      'El padrón de la federación: quién entra, con qué rol y desde cuándo. Sin límite de usuarios ni costo por asiento.',
    loadingWhat: 'el padrón',
    empty: 'El padrón está vacío. Creá el primer usuario para dejar de usar el token compartido.',
    newUser: 'Nuevo usuario',
    editing: 'Editando «{username}»',
    username: 'Usuario (minúsculas, números, punto, guión y guión bajo)',
    name: 'Nombre y apellido',
    role: 'Rol',
    passwordNew: 'Contraseña (mínimo {min} caracteres)',
    passwordEdit: 'Contraseña nueva (vacío = no se cambia)',
    passwordHint: 'Cambiar la contraseña cierra TODAS las sesiones abiertas de esa persona.',
    disabled: 'Cuenta bloqueada (no puede ingresar)',
    disabledHint: 'Bloquear cierra sus sesiones al instante; el usuario y su historial quedan.',
    colUser: 'Usuario',
    colName: 'Nombre',
    colRole: 'Rol',
    colStatus: 'Estado',
    colCreated: 'Alta',
    colActions: 'Acciones',
    statusActive: 'Activo',
    statusDisabled: 'Bloqueado',
    you: 'vos',
    edit: 'Editar',
    resetPassword: 'Resetear clave',
    revoke: 'Cerrar sesiones',
    block: 'Bloquear',
    unblock: 'Desbloquear',
    remove: 'Borrar',
    working: '…',
    confirmDelete:
      '¿Borrar a «{username}»? Se cierran sus sesiones y el usuario no se puede recuperar.',
    confirmRevoke: '¿Cerrar todas las sesiones de «{username}»? Va a tener que volver a ingresar.',
    createdNotice: 'Usuario «{username}» creado.',
    savedNotice: 'Usuario «{username}» guardado.',
    deletedNotice: 'Usuario «{username}» borrado.',
    revokedNotice: 'Se cerraron las sesiones de «{username}».',
    blockedNotice: 'Se bloqueó a «{username}» y se cerraron sus sesiones.',
    unblockedNotice: 'Se desbloqueó a «{username}».',
    revokeHint:
      'Cerrar sesiones se hace bloqueando y desbloqueando la cuenta: es lo único que hoy expone el API, así que quedan dos entradas en la auditoría.',
    revokeHalfway:
      'Se cerraron las sesiones de «{username}», pero la cuenta QUEDÓ BLOQUEADA: desbloqueala desde la lista ({reason}).',
    selfDeleteTitle: 'No podés borrar tu propio usuario.',
    forbidden: 'La administración del padrón es sólo para el rol admin.',
    forbiddenHint:
      'Tu rol es «{role}». El padrón lo maneja quien tenga el permiso «{permission}»: pedíselo a la federación.',
    anon: 'Ingresá con un usuario admin para administrar el padrón.',
    roleHintAdmin: 'admin — todo, incluido el padrón y la auditoría.',
    roleHintScout: 'estadístico — carga el partido y lee/exporta todo.',
    roleHintCoach: 'entrenador — lee todo (incluido el .dvw), no escribe eventos.',
    roleHintViewer: 'dirigencia/prensa — sólo los documentos ya digeridos.',
    errors: {
      usernameRequired: 'Falta el usuario (es la clave del padrón y va en la URL).',
      usernameInvalid: 'El usuario sólo admite minúsculas, números, punto, guión y guión bajo.',
      nameRequired: 'Falta el nombre de la persona.',
      passwordRequired: 'Al crear un usuario la contraseña es obligatoria.',
      passwordShort: 'La contraseña necesita al menos {min} caracteres.',
    },
  },

  settings: {
    title: 'Ajustes',
    connectionCard: 'Conexión con el API',
    apiUrl: 'URL del API (vacío = mismo origen que este portal)',
    token: 'Token (Bearer) — modo compatibilidad. Vacío si entrás con usuario.',
    tokenHint:
      'Es el token único de la federación (SCOUT_API_TOKEN), previo a los usuarios. Queda para integraciones: si iniciaste sesión, manda tu sesión y este campo se ignora.',
    sessionCard: 'Sesión',
    sessionActive: 'Estás dentro como {name} ({role}).',
    sessionNone: 'No hay sesión iniciada en este navegador.',
    sessionLogin: 'Ingresar con usuario',
    sessionLogout: 'Cerrar sesión',
    sessionUsingToken: 'Este navegador está usando el token de compatibilidad, no una sesión.',
    testConnection: 'Probar conexión',
    testing: 'Probando…',
    testOk: 'API v{version} · {count} partido(s) accesibles.',
    testBadToken: 'El API v{version} responde, pero rechazó el token: revisá Authorization.',
    testPartial: 'API v{version} responde (el listado devolvió: {reason}).',
    savedHint: 'El test usa la configuración YA GUARDADA. Si cambiaste algo, guardá primero.',
    themeCard: 'Diseño',
    themeLabel: 'Elegí el diseño',
    themeVar: 'Nuevo (VAR)',
    themeClasico: 'Clásico',
    languageCard: 'Idioma',
    languageLabel: 'Idioma de la interfaz',
    languageHint: 'Se aplica al instante y queda guardado en el localStorage de este navegador.',
    efficiencyCard: 'Convención de estadística',
    efficiencyIntro:
      'Cada cuerpo técnico calcula la eficiencia y la positividad con su convención. La elegida acá rige en TODAS las pestañas de estadística, en el reporte imprimible y en el acumulado de temporada.',
    efficiencyLabel: 'Fórmulas de eficiencia',
    efficiencyHint:
      'Se aplica al instante, como el idioma. No cambia el log ni ningún conteo: sólo con qué fórmula se dividen las evaluaciones ya cargadas.',
    replayCard: 'Server de replay (VAR)',
    replayIntro:
      'Es el server de video de LA CANCHA, en la LAN. El clip sale de ahí directo a este navegador, sin pasar por el API.',
    replayUrl: 'URL del server de replay (vacío = sin VAR en este navegador)',
    replayToken: 'Token del server de replay',
    replayHint:
      'Si el portal se sirve por HTTPS y el VAR es HTTP en la LAN, el navegador va a bloquear el pedido por contenido mixto: en cancha se abre el portal por HTTP, o el VAR se publica con TLS.',
    howCard: 'Cómo funciona',
    howLocal: 'Los ajustes viven en el localStorage de este navegador; no se sincronizan.',
    howLiveBefore: 'El live score se lee con ',
    howLiveMiddle: ' en modo streaming (no con',
    howLiveAfter: ') justamente para poder mandar el header de autorización.',
    howCorsBefore: 'Si el API está en otro dominio, tiene que permitir este origen por CORS (',
    howCorsAfter: ').',
  },

  api: {
    badRequest: 'El API rechazó el pedido (400).',
    unauthorized: 'El API pide autenticación: revisá el token en Ajustes.',
    forbidden: 'Tu rol no tiene permiso para esto (403). Cambiar el token en Ajustes no lo arregla: lo habilita la federación.',
    forbiddenPermission:
      'Tu rol no tiene el permiso «{permission}» que pide el API (403). Lo habilita la federación, no se arregla desde Ajustes.',
    notFound: 'No se encontró lo pedido en el API (404).',
    conflict: 'El partido está incompleto (409).',
    unprocessable: 'El reglamento del partido es desconocido (422).',
    notImplemented: 'El API todavía no puede generar esto (módulo pendiente).',
    serverError: 'Error del servidor ({status}).',
    unexpected: 'Respuesta inesperada del API ({status}).',
    noFetch: 'Este entorno no tiene fetch.',
    networkError: 'No se pudo conectar con el API ({reason}).',
    notJson: 'El API respondió algo que no es JSON.',
  },

  /**
   * PORTAL PÚBLICO (`/{slug}`). Es la cara de la federación: lo lee la prensa,
   * el club y el hincha, no el estadístico. Por eso el vocabulario es el del
   * diario deportivo ("Fecha 7", "En vivo") y no el de la mesa.
   */
  publicPortal: {
    title: 'Portal público',
    today: 'Hoy',
    todayHint: 'Lo que se juega hoy, en vivo y sin recargar la página.',
    todaySection: 'Partidos de hoy',
    upcoming: 'Próximos partidos',
    recent: 'Últimos resultados',
    competitions: 'Competencias',
    results: 'Resultados',
    resultsHint: 'El archivo completo de la temporada, del más nuevo al más viejo.',
    competition: 'Competencia',
    match: 'Partido',
    fixture: 'Fixture',
    standings: 'Posiciones',
    evolution: 'Evolución del marcador',
    setsTable: 'Parciales',
    pointsByReason: 'Cómo se ganaron los puntos',
    reason: 'Motivo',
    set: 'Set',
    total: 'Total',
    inPlay: 'en juego',
    statusLive: 'En vivo',
    statusWalkover: 'W.O.',
    statusPostponed: 'Postergado',
    statusCancelled: 'Suspendido',
    matchday: 'Fecha {matchday}',
    tbd: 'A definir',
    seeAll: 'Ver todos',
    teamsCount: '{count} equipos',
    matchesCount: '{count} partidos',
    filterCompetition: 'Competencia:',
    allCompetitions: 'Todas',
    noDate: 'Sin fecha',
    noMatchesToday: 'Hoy no hay partidos programados.',
    noCompetitions: 'Todavía no hay competencias publicadas.',
    noResults: 'Todavía no hay resultados cargados.',
    noFixture: 'El calendario de esta competencia todavía no se publicó.',
    noStandings: 'Esta competencia todavía no tiene tabla de posiciones.',
    noSets: 'El partido todavía no empezó.',
    noPoints: 'Todavía no se jugó ningún punto.',
    notStarted: 'El partido está programado y todavía no empezó.',
    pendingMatches: 'Faltan jugar {count} partidos de esta fase.',
    truncated: 'Se muestran los resultados más recientes.',
    position: 'Pos',
    team: 'Equipo',
    points: 'Pts',
    played: 'PJ',
    won: 'PG',
    lost: 'PP',
    setsFor: 'Sets',
    setRatio: 'Ratio sets',
    pointRatio: 'Ratio puntos',
    updated: 'Actualizado {ago}',
    agoSeconds: 'hace {seconds} s',
    agoMinutes: 'hace {minutes} min',
    retrying: 'reintentando',
    stale: 'No se pudo actualizar: lo que ves es la última información que llegó.',
    loadFailed: 'No se pudo cargar la información. Se vuelve a intentar solo.',
    notFoundTitle: 'No hay portal acá',
    notFoundBody: 'No existe un portal público en /{slug}, o la federación lo tiene apagado.',
    unknownRoute: 'Esta dirección no existe en el portal.',
    staffLink: 'Acceso de la federación',
    widgetHint: 'Para una transmisión:',
    widgetLink: 'marcador para OBS',
    widgetStale: 'Marcador sin actualizar',
    widgetLabel: '{home} {setsHome} - {setsAway} {away}, parcial {scoreHome} a {scoreAway}',
  },

  describe: {
    unknownPlayer: 's/n',
    matchCreated: 'Partido creado: {name}',
    setStarted: 'Empieza el set {set}',
    setEnded: 'Fin del set {set}: {home}–{away} ({team})',
    matchEnded: 'Fin del partido: gana {team} {setsHome}–{setsAway}',
    lineupSet: 'Formación de {team}: {rotation}',
    touch: '{team} · {skill} {evaluation} — {player}',
    touchZones: '{team} · {skill} {evaluation} — {player} ({startZone}→{endZone})',
    point: 'Punto {team} por {reason} — {home}–{away}',
    substitution: 'Cambio en {team}: entra #{playerIn} por #{playerOut}',
    substitutionLibero: 'Cambio en {team}: entra #{playerIn} por #{playerOut} (líbero)',
    timeoutTechnical: 'Tiempo técnico de {team}',
    timeoutRegular: 'Tiempo de {team}',
    sanction: 'Sanción ({card}) a {team}',
    comment: 'Nota: {text}',
    undo: 'Deshacer',
    correction: 'Corrección',
    officialsSet: 'Cuerpo arbitral cargado ({count})',
    toss: 'Sorteo: gana {team} y elige {choice}',
    tossDecider: 'Sorteo del set decisivo: gana {team} y elige {choice}',
    lineupApproved: 'Formación de {team} aprobada (set {set})',
    rallyStarted: 'Saca {team} · #{server}',
    challengeRequested: 'Challenge pedido por {team}',
    challengeOfficial: 'Revisión de oficio del 1er árbitro',
    challengeResolved: 'Challenge resuelto: {outcome} ({action})',
    replayBookmark: 'Marca de video: {label}',
    signature: 'Firma de {role}: {name}',
    sheetClosed: 'Acta cerrada ({outcome})',
  },

  court: {
    net: 'RED',
    libero: 'líbero',
    captain: 'capitán',
    serving: 'Saca',
    playerTitle: '#{number} en P{position}',
    roles: {
      setter: 'ARM',
      outside: 'PUN',
      opposite: 'OPU',
      middle: 'CEN',
      libero: 'LIB',
    },
  },

  mesaPanel: {
    autoRefresh: 'Al día',
    autoRefreshTitle: 'La mesa se actualiza sola cada pocos segundos mientras el acta está abierta.',
    scoreLabel: 'Marcador del partido',
    lineupsTitle: 'Formaciones en cancha · set {set}',
    courtLabel: 'Cancha: formaciones de {home} y {away}',
    flowLabel: 'Flujo del challenge',
    flow: {
      request: 'Pedido',
      reason: 'Motivo',
      outcome: 'Resultado',
      score: 'Marcador',
      confirm: 'Registrar',
    },
    flowHint: {
      request: 'Bocina del banco o de oficio',
      reason: 'La falta que se desafía',
      outcome: 'Uno de los cinco del VCS',
      score: 'Cómo queda el punto',
      confirm: 'Un solo lote al acta',
    },
    reviewTitle: 'Revisión',
    keeps: 'Retiene el challenge',
    loses: 'Pierde el challenge',
    confirmNeeds: 'Elegí el resultado y la acción sobre el marcador para habilitar el registro.',
    windowTitle: 'Ventana de {seconds} s',
    windowOpen: 'Quedan {seconds} s para pedir challenge',
    windowClosed: 'Cerrada: ya no se puede desafiar el último punto',
    windowReview: 'Revisión en curso',
    windowNone: 'Todavía no hay un punto que desafiar',
    windowHint: 'Art. 7 · cuenta desde que esta pantalla vio el punto',
    qrLabel: 'Código QR de la credencial de {holder}',
    showQr: 'Mostrar QR',
    hideQr: 'Ocultar QR',
    qrAgainHint: 'Escaneá con la cámara de la tablet: abre la consola con este rol. El PIN no se vuelve a mostrar.',
    qrElsewhere: 'El QR sólo se puede volver a mostrar en la laptop que emitió la credencial.',
  },

  benchPanel: {
    lineupHint: 'Armar la del próximo set',
    state: {
      PENDING: 'Pendiente',
      ACCEPTED: 'Aceptado',
      REJECTED: 'Rechazado',
      CANCELLED: 'Cancelado',
    },
  },

  matchViews: {
    kpiPoints: 'Puntos ({byPlayer} con autor)',
    kpiSideout: 'Sideout',
    kpiBreak: 'Break',
    kpiAttack: 'Ataque Ef%',
    kpiReception: 'Recepción +',
    attacksCount: '{count} ataques',
    zoneSub: '{count} · Ef {eff}',
    setterCourtTitle: 'Distribución por zona de salida',
    setterCourtAll: 'Todas',
    setterCourtGrade: 'R{grade}',
    setterCourtLabel: 'Cancha: distribución del armador de {team} — {what}',
    setterCourtOther:
      '{count} ataques sin zona de salida 1–6 (combinaciones sin zona o sin codificar): están en la tabla, no en la cancha.',
    setterZoneTitle: 'Zona {zone}: {count} ataques ({pct}), eficiencia {eff}',
    rotCourtSideout: 'Sideout por rotación',
    rotCourtBreak: 'Break por rotación',
    rotCourtLabel: 'Cancha: {what} de {team}',
    rotCourtHint:
      'Cada zona es una rotación: R1 es el armador en zona 1 (sacando), R2 en zona 2, y así. El color va de la peor a la mejor del equipo.',
    rotSub: '{won}/{total}',
    zonesCourtTitle: 'Sobre la cancha',
    zonesCourtLabel: 'Cancha: mapa de {skill} de {team}',
    zonesCourtHint:
      'Abajo, la cancha propia: de dónde salió, pintada por eficiencia. Arriba, la rival: adónde fue, pintada por cantidad. Tocá una zona propia para ver sólo lo que salió de ahí.',
    zonesCourtHintReception:
      'Abajo, la cancha propia: dónde se recibió, pintada por eficiencia. Arriba, la rival: de dónde vino el saque. Tocá una zona propia para ver sólo esas recepciones.',
    zonesCount: '{count} toques',
    zonesOwnTitle: 'Zona {zone}: {count} toques, eficiencia {eff}',
    zonesOtherTitle: 'Zona {zone}: {count} toques',
    zonesFiltering: 'Filtrando zona {zone}',
    zonesClear: 'Ver todas',
    zonesNoFilter: 'Todas las zonas',
    zonesOther: '{count} toques sin zona 1–6: están en la matriz, no en la cancha.',
    zonesMatrixTitle: 'Matriz salida × llegada',
  },
};

/** El diccionario español define la FORMA: en/pt tienen que calzar exacto. */
export type Messages = typeof es;
